import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, FileCheck2, Phone, ShieldCheck, Star, Zap } from "lucide-react";
import { GOOGLE_REVIEW_LINK, PHONE_DISPLAY, TEL_LINK, WA_LINK } from "@/lib/site";
import { MaskLine } from "./Reveal";
import { GoogleIcon, WhatsAppIcon } from "./icons";

const TRUST = [
  { icon: ShieldCheck, label: "Zero Hidden Charges" },
  { icon: Zap, label: "24–48hr Fast Approval" },
  { icon: FileCheck2, label: "Minimal Documents" },
];

export const Hero = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-36, 36]);

  return (
    <section id="home" className="relative overflow-hidden pt-32 sm:pt-40 pb-16 lg:pb-24">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-40 w-[540px] h-[540px] rounded-full bg-brand-light blur-3xl opacity-60"
      />
      <div
        aria-hidden="true"
        className="absolute top-64 -left-48 w-[420px] h-[420px] rounded-full bg-gold-light blur-3xl opacity-70"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-badge"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white/70 backdrop-blur px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
            </span>
            Trusted Loan Services • India
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="mt-6 font-display font-medium text-[2.75rem] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight text-ink"
          >
            <MaskLine delay={0.15}>Aapka Sapna,</MaskLine>
            <MaskLine delay={0.28}>
              Humara{" "}
              <em className="relative inline-block not-italic font-display italic text-gold">
                Sankalp.
                <motion.svg
                  viewBox="0 0 220 12"
                  className="absolute -bottom-1 left-0 w-full"
                  fill="none"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M4 9 C 60 2, 160 2, 216 7"
                    stroke="#15803D"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: "easeOut" }}
                  />
                </motion.svg>
              </em>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Home Loan, Business Loan, Personal Loan aur Education Loan — sabse low
            interest rate, fast approval aur 100% transparent process. Bas ek call
            kijiye, baaki hum sambhal lenge.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              data-testid="hero-call-cta"
              href={TEL_LINK}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-ink text-white px-7 py-4 text-base font-semibold hover:bg-brand transition-colors duration-300"
            >
              <span className="grid place-items-center w-7 h-7 rounded-full bg-white/10 group-hover:bg-white/15 transition-colors">
                <Phone className="w-3.5 h-3.5" />
              </span>
              Call Karein: {PHONE_DISPLAY}
            </a>
            <a
              data-testid="hero-whatsapp-cta"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-brand/20 bg-brand-light/60 text-brand-dark px-7 py-4 text-base font-semibold hover:bg-brand hover:text-white hover:border-brand transition-colors duration-300"
            >
              <WhatsAppIcon className="w-5 h-5" />
              WhatsApp Par Baat Karein
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600"
              >
                <Icon className="w-4 h-4 text-brand" />
                {label}
              </span>
            ))}
          </motion.div>

          <motion.a
            data-testid="hero-google-review-badge"
            href={GOOGLE_REVIEW_LINK}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white border border-black/5 shadow-sm hover:shadow-md px-5 py-3.5 transition-shadow"
          >
            <GoogleIcon className="w-6 h-6" />
            <span className="text-left">
              <span className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 text-gold fill-gold" />
                ))}
              </span>
              <span className="block text-xs font-medium text-slate-500 mt-0.5">
                Google par humein review dijiye
              </span>
            </span>
            <ArrowUpRight className="w-4 h-4 text-slate-400" />
          </motion.a>
        </div>

        <div className="lg:col-span-5" ref={imgRef}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/5] shadow-2xl shadow-ink/20">
              <motion.img
                data-testid="hero-image"
                src="https://images.unsplash.com/photo-1657912230234-87f45165424d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBmYW1pbHklMjBob21lJTIwbG9hbiUyMGhhcHB5JTIwaG91c2UlMjBwdXJjaGFzZXxlbnwwfHx8fDE3ODk4Njg0MDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Indian family apne naye ghar ke saath khush"
                style={{ y, scale: 1.15 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
            </div>

            <div
              data-testid="hero-rate-chip"
              className="absolute top-5 left-5 rounded-2xl bg-white/90 backdrop-blur px-5 py-3.5 shadow-lg border border-black/5"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Rate from
              </p>
              <p className="font-display text-2xl font-semibold text-ink">
                8.35<span className="text-base align-top">%</span>{" "}
                <span className="text-xs font-sans font-normal text-slate-500">p.a.*</span>
              </p>
            </div>

            <div
              data-testid="hero-approval-chip"
              className="absolute -bottom-4 right-4 sm:right-8 rounded-2xl bg-ink text-paper px-5 py-3.5 shadow-xl flex items-center gap-3"
            >
              <span className="grid place-items-center w-9 h-9 rounded-full bg-brand">
                <Zap className="w-4 h-4 text-white" />
              </span>
              <span>
                <span className="block text-sm font-semibold">Fast Approval</span>
                <span className="block text-xs text-paper/60">24–48 ghante mein</span>
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
